import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const CartPage = () => {
    const navigate = useNavigate();
    const { cart, loading, updateQuantity, removeFromCart } = useCart();

    const handleUpdateQuantity = async (productId, currentQty, delta) => {
        const newQty = currentQty + delta;
        if (newQty < 1) {
            await removeFromCart(productId);
            toast.success('Prodotto rimosso dal carrello');
        } else {
            await updateQuantity(productId, newQty);
        }
    };

    const handleRemove = async (productId) => {
        await removeFromCart(productId);
        toast.success('Prodotto rimosso dal carrello');
    };

    if (loading) {
        return (
            <div className="min-h-screen py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold mb-8">Il tuo carrello</h1>
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-surface rounded-xl h-32 animate-pulse" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (cart.items.length === 0) {
        return (
            <div className="min-h-screen py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold mb-8">Il tuo carrello</h1>
                    <div className="text-center py-16 bg-surface rounded-xl border border-border">
                        <ShoppingBag className="w-16 h-16 mx-auto text-muted mb-4" />
                        <h2 className="text-xl font-semibold mb-2">Carrello vuoto</h2>
                        <p className="text-muted mb-6">Non hai ancora aggiunto prodotti al carrello</p>
                        <Link to="/store">
                            <Button className="btn-primary">
                                Vai allo store
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-3xl font-bold mb-8" data-testid="cart-title">Il tuo carrello</h1>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Cart Items */}
                    <div className="lg:col-span-2 space-y-4">
                        {cart.items.map((item) => (
                            <div
                                key={item.product_id}
                                className="bg-surface border border-border rounded-xl p-4 flex gap-4"
                                data-testid={`cart-item-${item.product_id}`}
                            >
                                <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-surface-elevated">
                                    <img
                                        src={item.product?.image_url}
                                        alt={item.product?.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <Link 
                                        to={`/store/${item.product_id}`}
                                        className="font-semibold hover:text-primary transition-colors line-clamp-1"
                                    >
                                        {item.product?.name}
                                    </Link>
                                    <p className="text-sm text-muted mt-1">
                                        €{item.product?.price.toFixed(2)} cad.
                                    </p>
                                    <div className="flex items-center justify-between mt-3">
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="w-8 h-8 border-border"
                                                onClick={() => handleUpdateQuantity(item.product_id, item.quantity, -1)}
                                                data-testid={`decrease-${item.product_id}`}
                                            >
                                                <Minus className="w-4 h-4" />
                                            </Button>
                                            <span className="w-8 text-center font-medium">{item.quantity}</span>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="w-8 h-8 border-border"
                                                onClick={() => handleUpdateQuantity(item.product_id, item.quantity, 1)}
                                                data-testid={`increase-${item.product_id}`}
                                            >
                                                <Plus className="w-4 h-4" />
                                            </Button>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="text-danger hover:text-danger hover:bg-danger/10"
                                            onClick={() => handleRemove(item.product_id)}
                                            data-testid={`remove-${item.product_id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-primary">
                                        €{item.item_total?.toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-surface border border-border rounded-xl p-6 sticky top-24">
                            <h2 className="text-xl font-semibold mb-4">Riepilogo ordine</h2>
                            
                            <div className="space-y-3 mb-6">
                                <div className="flex justify-between text-muted">
                                    <span>Subtotale</span>
                                    <span>€{cart.total.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-muted">
                                    <span>Spedizione</span>
                                    <span>{cart.total >= 50 ? 'Gratuita' : '€4.99'}</span>
                                </div>
                                <div className="border-t border-border pt-3 flex justify-between font-bold text-lg">
                                    <span>Totale</span>
                                    <span className="text-primary" data-testid="cart-total">
                                        €{(cart.total + (cart.total >= 50 ? 0 : 4.99)).toFixed(2)}
                                    </span>
                                </div>
                            </div>

                            <Button
                                className="w-full btn-primary"
                                onClick={() => navigate('/checkout')}
                                data-testid="checkout-btn"
                            >
                                Procedi al checkout
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>

                            <Link to="/store" className="block text-center mt-4 text-muted hover:text-primary text-sm">
                                Continua lo shopping
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartPage;
